import React, { useCallback, useState } from 'react';
import { Button, Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNavigation } from '../../../common/navigation';
import {
  CreateUpdateClientGroupDto,
  GetClientGroupDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { ClientGroupModalEditor } from '../../components/ClientGroups/ClientGroupModalEditor';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  validate,
  convertClientGroup,
  newClientGroup,
} from '../../../common/pokCore/contexts/ClientGroupContext';
import { ClientGroupSearch } from '../../components/ClientGroups/ClientGroupSearch';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';

export const ClientGroupsView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const [clientGroup, setClientGroup] = useState<GetClientGroupDto>();
  const [showNew, setShowNew] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const { t, tk } = useTranslation('clientLng');

  const handleAdd = () => {
    setClientGroup(undefined);
    setShowNew(true);
  };

  const addEditClientGroup = async (
    dto: CreateUpdateClientGroupDto,
    id?: string,
  ) => {
    const status = await validate(dto);
    if (status.valid) {
      try {
        if (id) {
          await pok.clientGroups.update(id, dto);
          setShowNew(false);
          setRefresh(!refresh);
        } else {
          const created = await pok.clientGroups.create(dto);
          setShowNew(false);
          setRefresh(!refresh);
          if (created?.id) {
            nav.clientGroup(created.id);
          }
        }
        return { saved: true, errors: [] };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return { saved: false, errors: status.errors };
    }
  };

  const getData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientGroups.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [pok.clientGroups],
  );

  const getCount = useCallback(
    ((filterText: string, filterColumns: string) =>
      pok.clientGroups.getAllCount(
        filterText,
        filterColumns,
      )) as GridCountFunction,
    [pok.clientGroups],
  );

  const handleClick = (id: string) => {
    nav.clientGroup(id);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.clientGroup.clientGroups)}
        </Card.Title>
        <Button variant="outline-primary" onClick={handleAdd} className="mb-3">
          {t(tk.clientGroup.clientGroupNew)}
        </Button>
        <ClientGroupSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleClick}
          refresh={refresh}
        />
        <ClientGroupModalEditor
          id={clientGroup?.id}
          systemNumber={clientGroup?.systemNumber}
          clientGroup={
            clientGroup ? convertClientGroup(clientGroup) : newClientGroup()
          }
          onCompleted={addEditClientGroup}
          show={showNew}
          handleClose={() => setShowNew(false)}
        />
      </Card.Body>
    </Card>
  );
};
